import { useEffect, useState } from 'react';
import { fetchTokenizerToken } from '../services/tokenService';
import appConfigJson from '../config/app-config.json';
import type { AppConfig } from '../types/appConfig';


interface UseAuthTokenOptions {
  tokenUrl: string;
  scope: string;
  country?: string;
  refreshMs?: number;
  clientType?: string;
  sid?: string;
}


const useAuthToken = ({
  tokenUrl,
  scope,
  country,
  refreshMs = 45 * 60 * 1000, // 45 minutes
  clientType,
  sid,
}: UseAuthTokenOptions) => {
  const [authToken, setAuthToken] = useState<string>('');
  const [error, setError] = useState<boolean>(false);

  const appConfig = appConfigJson as AppConfig;
  const getToken = async () => {
    // iOS flow with sid: use tokenizer endpoint
    if (clientType === 'iOS' && sid) {
      try {
        const url = appConfig.tokenizerGetTokenUrl;
        if (!url) throw new Error('tokenizerGetTokenUrl not configured');
        const token = await fetchTokenizerToken(sid, url);
        setAuthToken(token);
        setError(false);
      } catch {
        setAuthToken('');
        setError(true);
      }
      return;
    }
    // Legacy/other flow
    if (!tokenUrl) return;
    try {
      const response = await fetch(tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(country ? { 'country-code': country } : {}),
        },
        body: JSON.stringify({
          CallerName: 'AutoRenewUI', // required by TokenProxy-API
          Scopes: [scope],
          ClientIdentifier: 'autoRenew' // required by TokenProxy-API
        }),
      });
      if (!response.ok) throw new Error('Failed to authenticate');
      const data = await response.text();
      setAuthToken(`${data}`);
      setError(false);
    } catch {
      setAuthToken('');
      setError(true);
    }
  };


  useEffect(() => {
    getToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenUrl, scope, country, clientType, sid]);


  useEffect(() => {
    const timer = setInterval(getToken, refreshMs);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenUrl, scope, country, refreshMs, clientType, sid]);

  return { authToken, authError: error };
};

export default useAuthToken;
